import cx from "classnames";
import {
  FunctionComponent,
  SVGProps,
  useEffect,
  useRef,
  useState,
} from "react";

import { GiftCardInclusionDetails } from "@/modules/giftCards/GiftCardInclusionDetails/GiftCardInclusionDetails";
import { formatCurrency } from "@/toolbox/Money";
import { DiscountType, DiscountUse, Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import ChevronDownSmall from "atoms/icon/icons/ic_chevron_down_small_ol.svg";
import ChevronUpSmall from "atoms/icon/icons/ic_chevron_up_small_ol.svg";
import QrCode from "atoms/icon/icons/qr_code.svg";
import { InclusionDetails } from "molecules/inclusion/InclusionDetails";

interface DiscountDetailsProps {
  inclusions?: Models<"InclusionListingsModel">;
  discount?: Models<"DiscountModel">;
  icon: FunctionComponent<SVGProps<SVGSVGElement>>;
  title: string;
  customDisclaimer?: string;
  itemType: "reward" | "offer";
  isBuyXGetY?: boolean;
  meetsRewardMinSpendRequirement?: boolean;
  rewardMinimumSpendInCents?: number;
  headerClassName?: string;
  disableNavigation?: boolean;
}

export const DiscountDetails = ({
  inclusions,
  discount,
  icon,
  title,
  customDisclaimer,
  itemType,
  isBuyXGetY = false,
  meetsRewardMinSpendRequirement = false,
  rewardMinimumSpendInCents = 0,
  headerClassName,
  disableNavigation = false,
}: DiscountDetailsProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [showMore, setShowMore] = useState(true);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const isMultipleUse = discount?.useType === DiscountUse.Unlimited;
  const isSingleUse =
    discount?.useType === DiscountUse.Once ||
    discount?.useType === DiscountUse.OncePerPerson;
  useEffect(() => {
    if (ref.current) {
      const inclusionsListHeight = ref.current.offsetHeight;
      if (inclusionsListHeight >= 500) {
        setShowMore(false);
        setShowMoreButton(true);
      }
    }
  }, [ref]);

  const discountTypeDisclaimer =
    discount?.type === DiscountType.Cash
      ? `$${
          discount.amountOff / 100
        } saving applies to your total purchase amount, not per individual item.`
      : "";

  const renderInclusions = (
    inclusions: Models<"InclusionListingsModel"> | undefined
  ) => {
    if (!inclusions) {
      return null;
    }

    return (
      inclusions &&
      Object.entries(inclusions).map(([key, value]) => {
        const isValueArray = Array.isArray(value);
        const isGiftCard = key === "giftCards";
        return (
          isValueArray &&
          value.length > 0 && (
            <div key={key}>
              <div className="py-2 pl-1 font-bold capitalize text-primary">
                {key === "memberships" ? "Memberships (1st month only)" : key}
              </div>
              <ul className="ml-6 list-disc">
                {isGiftCard ? (
                  <GiftCardInclusionDetails key={key} giftCards={value} />
                ) : (
                  value.map((item) => {
                    const typedItem =
                      key === "treatments"
                        ? (item as Models<"TreatmentInclusionModel">)
                        : (item as Models<"InclusionModel">);

                    return (
                      <InclusionDetails
                        key={typedItem.id}
                        typedItem={typedItem}
                        inclusionType={key}
                        disableNavigation={disableNavigation}
                      />
                    );
                  })
                )}
              </ul>
            </div>
          )
        );
      })
    );
  };

  const disclaimers = [
    {
      active: isMultipleUse,
      text: `This ${itemType} can be used multiple times.`,
    },
    {
      active: isSingleUse,
      text: `This ${itemType} can only be used once.`,
    },
    {
      active: !meetsRewardMinSpendRequirement && itemType === "reward",
      text: `${formatCurrency(
        rewardMinimumSpendInCents
      )} minimum purchase required.`,
    },
  ];

  const renderDisclaimerParagraph = () => (
    <p className="text-center text-body2 italic text-secondary">
      {disclaimers
        .filter((disclaimer) => disclaimer.active)
        .map((disclaimer) => disclaimer.text)
        .join(" ")}
    </p>
  );
  return (
    <>
      <div
        className={cx("mb-4 rounded bg-two px-4 py-5", headerClassName)}
        ref={ref}
      >
        <div className="mb-1 flex items-center gap-3">
          <div>
            <Icon
              svg={!discount ? QrCode : icon}
              size="xsmall"
              color="brandColor"
            />
          </div>
          <div className="text-bold1 text-primary">{title}</div>
        </div>
        {!isBuyXGetY && (
          <div
            className={cx(
              "relative flex flex-col overflow-hidden",
              !showMore && "max-h-[340px]"
            )}
          >
            {renderInclusions(inclusions)}

            {!showMore && (
              <div className="absolute bottom-0 h-10 w-full bg-gradient-to-t from-background-two to-transparent" />
            )}
          </div>
        )}

        {showMoreButton && (
          <button
            className="flex cursor-pointer pt-[6px]"
            onClick={() => setShowMore((x) => !x)}
          >
            <Icon
              svg={!showMore ? ChevronDownSmall : ChevronUpSmall}
              size="small"
              color="brandColor"
            />
            <div className="ml-3 text-bold1 text-brand-color">
              {!showMore ? "Show More" : "Show Less"}
            </div>
          </button>
        )}
      </div>
      <div className="flex flex-col gap-2">
        {!isBuyXGetY && discountTypeDisclaimer && (
          <p className="text-center text-body2 italic text-secondary">
            {discountTypeDisclaimer}
          </p>
        )}

        {renderDisclaimerParagraph()}

        {customDisclaimer && (
          <p className="text-center text-body2 italic text-secondary">
            {customDisclaimer}
          </p>
        )}
      </div>
    </>
  );
};
